import { io } from "socket.io-client";
import Axios from "../../networking/interceptor";
import { authOTPVerified } from "../../storage/authToken";
import * as routeNames from "../../routes/routeName";

const DRIVER_CLINIC_CALL_EVENT = "driver-clinic-call-updated";
const DEFAULT_TOKEN_TTL_SECONDS = 3600;
const MIN_REFRESH_DELAY_SECONDS = 10;
const REFRESH_RETRY_SECONDS = 30;

let socket = null;
let refreshTimer = null;

const clearRefreshTimer = () => {
	if (refreshTimer) {
		window.clearTimeout(refreshTimer);
		refreshTimer = null;
	}
};

const getRefreshDelaySeconds = (ttlSeconds) => {
	const ttl = Number(ttlSeconds) || DEFAULT_TOKEN_TTL_SECONDS;
	const buffer = ttl > 600 ? 300 : Math.max(30, Math.floor(ttl * 0.2));
	return Math.max(MIN_REFRESH_DELAY_SECONDS, ttl - buffer);
};

const getRealtimeToken = async (pharmacyId) => {
	if (!pharmacyId) {
		throw new Error("pharmacy_id is required for realtime connection.");
	}

	const response = await Axios.post("driver/realtime/token", {
		pharmacy_id: pharmacyId,
	});
	const {
		token,
		rooms = [],
		realtimeUrl,
		ttlSeconds,
		environment,
		roomPrefix,
	} = response?.data || {};

	if (!token) {
		throw new Error("Realtime token endpoint did not return a token.");
	}

	return {
		token,
		rooms,
		realtimeUrl: realtimeUrl || "",
		ttlSeconds: ttlSeconds || DEFAULT_TOKEN_TTL_SECONDS,
		environment: environment || "staging",
		roomPrefix: roomPrefix || `${environment || "staging"}:`,
	};
};

const isDriverRealtimeAllowed = () => {
	if (Number(authOTPVerified()) !== 1) return false;

	if (typeof window !== "undefined" && window.location?.pathname === routeNames.verify_otp) {
		return false;
	}

	return true;
};

export const subscribeToDriverClinicCallSocketUpdates = ({
	pharmacyId,
	onEvent,
	onStatus,
} = {}) => {
	let closed = false;

	const emitStatus = (detail) => {
		if (typeof onStatus === "function") {
			onStatus(detail);
		}
	};

	const emitEvent = (payload) => {
		if (typeof onEvent === "function") {
			onEvent(payload);
		}
	};

	const scheduleRefreshRetry = () => {
		clearRefreshTimer();
		refreshTimer = window.setTimeout(() => {
			if (closed) return;

			createConnection({ refresh: true }).catch((error) => {
				emitStatus({
					status: "refresh_error",
					message: error?.response?.data?.message || error?.message || "Realtime token refresh failed",
				});
				scheduleRefreshRetry();
			});
		}, REFRESH_RETRY_SECONDS * 1000);
	};

	const createConnection = async ({ refresh = false } = {}) => {
		const tokenDetail = await getRealtimeToken(pharmacyId);
		const { token, rooms, realtimeUrl, ttlSeconds, environment, roomPrefix } = tokenDetail;

		if (closed) return;

		const previousSocket = socket;
		const nextSocket = io(realtimeUrl, {
			auth: { token },
			transports: ["websocket"],
		});

		const statusBase = {
			rooms,
			ttlSeconds,
			environment,
			roomPrefix,
			refresh,
			nextRefreshSeconds: getRefreshDelaySeconds(ttlSeconds),
		};

		nextSocket.on("connect", () => {
			socket = nextSocket;

			if (previousSocket && previousSocket !== nextSocket) {
				previousSocket.removeAllListeners();
				previousSocket.close();
			}

			emitStatus({
				status: "connected",
				socketId: nextSocket.id,
				...statusBase,
			});
		});

		nextSocket.on("disconnect", (reason) => {
			if (socket === nextSocket) {
				emitStatus({
					status: "disconnected",
					reason,
					...statusBase,
				});
			}
		});

		nextSocket.on("connect_error", (error) => {
			emitStatus({
				status: refresh ? "refresh_error" : "connect_error",
				message: error?.message,
				...statusBase,
			});

			if (refresh) {
				nextSocket.removeAllListeners();
				nextSocket.close();
				scheduleRefreshRetry();
			}
		});

		nextSocket.on(DRIVER_CLINIC_CALL_EVENT, emitEvent);

		if (!refresh) {
			socket = nextSocket;
		}

		clearRefreshTimer();
		refreshTimer = window.setTimeout(() => {
			if (closed) return;

			emitStatus({
				status: "refreshing",
				...statusBase,
			});

			createConnection({ refresh: true }).catch((error) => {
				emitStatus({
					status: "refresh_error",
					message: error?.response?.data?.message || error?.message || "Realtime token refresh failed",
					...statusBase,
				});
				scheduleRefreshRetry();
			});
		}, getRefreshDelaySeconds(ttlSeconds) * 1000);
	};

	createConnection().catch((error) => {
		emitStatus({
			status: "connect_error",
			message: error?.response?.data?.message || error?.message || "Realtime connection failed",
		});
	});

	return {
		close: () => {
			closed = true;
			clearRefreshTimer();

			if (socket) {
				socket.removeAllListeners();
				socket.close();
				socket = null;
			}
		},
	};
};

// --- Shared multiplexed driver socket (Socket.IO) ---------------------------
// One connection carrying every driver realtime event we subscribe to. The backend
// publishes these alongside Ably, and Ably is unreliable inside the Android WebView
// (retired default host -> comet churn), so this is the path that actually works
// there. Kept separate from the clinic-call socket above so neither can break the
// other.
//
// The driver's token grants BOTH rooms (see backend DriverRealtimeController):
//   pharmacy:<pharmacyId>       -> pharmacy-wide events (in-store delivery, chat)
//   delivery-driver:<driverId>  -> driver-targeted events (handover)

const DRIVER_HANDOVER_EVENT = "delivery-handover:updated";
const DELIVERY_VIDEO_CALL_EVENT = "delivery-video-call:updated";

let muxSocket = null;
let muxRefreshTimer = null;
let muxPharmacyId = null;
let muxConnecting = false;
const muxListeners = new Map(); // event -> Set<handler>

const clearMuxRefreshTimer = () => {
	if (muxRefreshTimer) {
		window.clearTimeout(muxRefreshTimer);
		muxRefreshTimer = null;
	}
};

const muxHandlerFor = (event) => (payload) => {
	const handlers = muxListeners.get(event);
	if (!handlers) return;
	handlers.forEach((handler) => {
		try {
			handler(payload);
		} catch (_) {}
	});
};

const muxBindEvents = (socket) => {
	muxListeners.forEach((_handlers, event) => {
		socket.off(event);
		socket.on(event, muxHandlerFor(event));
	});
};

const muxTeardown = () => {
	clearMuxRefreshTimer();
	if (muxSocket) {
		muxSocket.removeAllListeners();
		muxSocket.close();
		muxSocket = null;
	}
	muxPharmacyId = null;
};

const muxScheduleRefresh = (pharmacyId, delaySeconds) => {
	clearMuxRefreshTimer();
	muxRefreshTimer = window.setTimeout(() => {
		if (muxListeners.size === 0 || !isDriverRealtimeAllowed()) return;
		muxConnect(pharmacyId).catch(() => {
			muxScheduleRefresh(pharmacyId, REFRESH_RETRY_SECONDS);
		});
	}, delaySeconds * 1000);
};

async function muxConnect(pharmacyId) {
	if (muxConnecting || !isDriverRealtimeAllowed()) return;
	muxConnecting = true;
	try {
		const { token, realtimeUrl, ttlSeconds } = await getRealtimeToken(pharmacyId);
		// Everyone may have unsubscribed while the token request was in flight.
		if (muxListeners.size === 0 || !isDriverRealtimeAllowed()) return;

		const previous = muxSocket;
		const next = io(realtimeUrl, { auth: { token }, transports: ["websocket"] });

		next.on("connect", () => {
			if (previous && previous !== next) {
				previous.removeAllListeners();
				previous.close();
			}
		});

		muxBindEvents(next);
		muxSocket = next;
		muxPharmacyId = pharmacyId;

		muxScheduleRefresh(pharmacyId, getRefreshDelaySeconds(ttlSeconds));
	} finally {
		muxConnecting = false;
	}
}

/**
 * Subscribe to one driver realtime event over the shared socket.
 * Returns { close } which removes only this listener; the socket stays up while
 * other listeners remain, and is torn down once the last one goes.
 */
export const subscribeToDriverRealtimeEvent = ({ pharmacyId, event, onEvent } = {}) => {
	const noop = { close: () => {} };
	if (!pharmacyId || !event || typeof onEvent !== "function") return noop;
	if (!isDriverRealtimeAllowed()) return noop;

	let handlers = muxListeners.get(event);
	if (!handlers) {
		handlers = new Set();
		muxListeners.set(event, handlers);
	}
	handlers.add(onEvent);

	if (muxSocket && muxPharmacyId === pharmacyId) {
		// Socket already up for this pharmacy - just (re)bind this event.
		muxSocket.off(event);
		muxSocket.on(event, muxHandlerFor(event));
	} else {
		if (muxSocket && muxPharmacyId !== pharmacyId) muxTeardown();
		muxConnect(pharmacyId).catch(() => {});
	}

	return {
		close: () => {
			const set = muxListeners.get(event);
			if (!set) return;
			set.delete(onEvent);
			if (set.size === 0) {
				muxListeners.delete(event);
				try {
					muxSocket?.off(event);
				} catch (_) {}
			}
			if (muxListeners.size === 0) muxTeardown();
		},
	};
};

/** Handover request/response (drives the Route Control -> Incoming pop-up). */
export const subscribeToDriverHandoverSocketUpdates = ({ pharmacyId, onEvent } = {}) =>
	subscribeToDriverRealtimeEvent({ pharmacyId, event: DRIVER_HANDOVER_EVENT, onEvent });

/** In-store / other-driver delivery ("... was delivered by the In-Store" toast). */
export const subscribeToDeliveredDispenseSocketUpdates = ({ pharmacyId, onEvent } = {}) =>
	subscribeToDriverRealtimeEvent({
		pharmacyId,
		event: `delivered-dispense-${pharmacyId}`,
		onEvent,
	});

/** New chat message notification for this pharmacy user. */
export const subscribeToMessageNotificationSocketUpdates = ({ pharmacyId, userId, onEvent } = {}) =>
	subscribeToDriverRealtimeEvent({
		pharmacyId,
		event: `message-notification-${pharmacyId}-${userId}`,
		onEvent,
	});

/** Delivery video-call signalling, dual-published with Ably during migration. */
export const subscribeToDeliveryVideoCallSocketUpdates = ({ pharmacyId, onEvent } = {}) =>
	subscribeToDriverRealtimeEvent({
		pharmacyId,
		event: DELIVERY_VIDEO_CALL_EVENT,
		onEvent,
	});
